import React, { useState, useCallback } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import LeftNavigation from './LeftNavigation';
import ClientSwitcher from './ClientSwitcher';
import NoWorkspaceAccess from './NoWorkspaceAccess';
import { ProfileProvider, useProfile } from '../../contexts/ProfileContext';
import { ContactProvider } from '../../contexts/ContactContext';
import { SettingsProvider } from '../../contexts/SettingsContext';
import { useNavigation, ViewType } from '../../hooks/useNavigation';
import { useKeyboardNavigation } from '../../hooks/useKeyboardNavigation';
import SettingsPanel from '../Profile/SettingsPanel';
import { TestChatProvider } from '../../contexts/TestChatContext';
import { WhatsAppIcon } from '../UI/Icons';
import { useClient } from '../../contexts/ClientContext';
import { ChatProvider, deactivateChatContext } from '../../contexts/ChatContext';

const DashboardContent: React.FC = () => {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [showTestChat, setShowTestChat] = useState(false);
  const { profile } = useProfile();
  const { accessibleClients, isLoading } = useClient();
  const {
    navigationState,
    handleViewChange,
    toggleNavigation,
    getViewInfo,
    canGoBack,
    goToPreviousView,
    validViews,
  } = useNavigation();

  const currentViewInfo = getViewInfo(navigationState.activeView);

  // Wrap handleViewChange to deactivate ChatContext BEFORE navigating away from WhatsApp.
  // Uses module-level deactivateChatContext() instead of useChat() to avoid subscribing
  // DashboardContent to ChatContext — subscribing would cause ChatContext state changes
  // (during prefetching/polling) to re-render DashboardContent, which preempts React
  // Router's transition and prevents the Outlet from switching components.
  const wrappedHandleViewChange = useCallback((view: ViewType) => {
    if (view !== 'whatsapp' && navigationState.activeView === 'whatsapp') {
      deactivateChatContext();
    }
    handleViewChange(view);
  }, [handleViewChange, navigationState.activeView]);

  // Determine page-specific width: 80% for WhatsApp (chat) pages, 60% otherwise
  const location = useLocation();
  const isChatPage = location.pathname.startsWith('/whatsapp');
  const isAIPersonaPage = location.pathname === '/ai-persona';
  const isAgentTriggerPage = location.pathname === '/agent-triggers';
  const isAddonPage = location.pathname === '/addons';

  const isWhatsAppSuite = location.pathname.startsWith('/whatsapp') || 
                          location.pathname.startsWith('/ai-persona') ||
                          location.pathname.startsWith('/send-templates') ||
                          location.pathname.startsWith('/campaign-history') ||
                          location.pathname.startsWith('/templates') ||
                          location.pathname.startsWith('/agent-triggers') ||
                          location.pathname.startsWith('/agent-reports');

  const pageWidthClass = isChatPage
    ? 'w-[100%]'
    : isAIPersonaPage
    ? 'w-[95%]'
    : isAgentTriggerPage
    ? 'w-[100%]'
    : isAddonPage
    ? 'w-[92%]'
    : 'w-[92%]';

  const handleSettingsClick = () => {
    setIsSettingsOpen(true);
  };

  const handleSettingsClose = () => {
    setIsSettingsOpen(false);
  };

  // Enable keyboard navigation
  useKeyboardNavigation({
    activeView: navigationState.activeView,
    onViewChange: wrappedHandleViewChange,
    canGoBack,
    onGoBack: goToPreviousView,
    validViews,
    onToggleNavigation: toggleNavigation,
  });

  const logoSrc = profile?.logoUrl || '/logo.png';

  // Show "No Workspace Access" page if user has no clients and loading is complete
  if (!isLoading && accessibleClients.length === 0) {
    return <NoWorkspaceAccess />;
  }

  return (
    <div className="h-screen bg-primary-bg flex overflow-hidden">
      {/* Left Navigation Panel */}
      <div className="w-64 transition-all duration-300 ease-in-out bg-sidebar-bg flex-shrink-0">
        <LeftNavigation
          activeView={navigationState.activeView}
          onViewChange={wrappedHandleViewChange}
          onSettingsClick={handleSettingsClick}
          logoSrc={logoSrc}
          title={profile?.businessName || 'AI Suite'}
          subtitle="Business Automation"
        />
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        
        {/* Header - Conditional Rendering */}
        <header className="bg-white shadow-sm border-b border-gray-200 px-8 py-4 h-[72px] flex items-center justify-between">
            <div className="flex items-center">
              {isWhatsAppSuite && (
                <div className="flex items-center space-x-3 transition-opacity duration-300 ease-in-out animate-fadeIn">
                   <div className="p-2 bg-green-100 rounded-lg">
                      <WhatsAppIcon className="w-6 h-6 text-[#25D366]" />
                   </div>
                   <div className="flex flex-col">
                      <h2 className="text-lg font-semibold text-gray-800 leading-none">WhatsApp AI Suite</h2>
                   </div>
                </div>
              )}
            </div>
            
            {/* Client Switcher - Only show if user has access to multiple clients */}
            {accessibleClients.length > 1 && (
              <ClientSwitcher />
            )}
        </header>

        {/* Content Area with Router Outlet */}
        <main className="flex-1 p-8 overflow-auto bg-primary-bg">
          <div className={`
            ${pageWidthClass} mx-auto h-full
            transition-all duration-300 ease-in-out
          `}>
            <Outlet />
          </div>
        </main>
      </div>
      
      {/* Settings Panel Modal */}
      <SettingsPanel
        isOpen={isSettingsOpen}
        onClose={handleSettingsClose}
      />
    </div>
  );
};

const DashboardLayout: React.FC = () => {
  return (
    <SettingsProvider>
      <ProfileProvider>
        <ContactProvider>
          <TestChatProvider>
            <ChatProvider>
              <DashboardContent />
            </ChatProvider>
          </TestChatProvider>
        </ContactProvider>
      </ProfileProvider>
    </SettingsProvider>
  );
};

export default DashboardLayout;